import {
  Button, Col, Form, Modal, notification, Row, Space, Tag, Typography
} from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useCallback, useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import TableWithSorter from '../../components/Common/TableWithSorter';
import { fetchCourseApplication, getCourses, sendPaymentLink, submitApplicationStatus, updateApplication } from '../../services/courseService';
import { getDaysDifferenceBetweenDates } from '../../utilities/dateHelper';
import CourseApplicationsForm from './components/CourseApplicationsForm.jsx';
import CourseApplicationsModal from './components/CourseApplicationsModal.jsx';
import {
  applicationStatus,
  defaultPageSize,
  sortOrderList
} from './constant';
import {
  getformattedCourseApplicationForCSV,
  isInternalEmail
} from './utils';

const { Title } = Typography;

const CourseApplicationsPage = () => {
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [courseList, setCourseList] = useState([]);

  const [filters, setFilters] = useState({
    courseCode: null,
    type: null,
    sortOrder: sortOrderList[0].value,
    startDate: null,
    endDate: null,
    sortBy: 'registrationTime',
    status: null
  });
  const [applicationList, setApplicationList] = useState(false);
  const [activeApplication, setActiveApplication] = useState(null);

  const [csvDataList, setCsvDataList] = useState([]);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [isCsvDataLoading, setIsCsvDataLoading] = useState(false);

  const columns = [
    {
      title: 'No',
      dataIndex: 'key',
      key: 'key',
      render: (value, item, index) => (pageNo - 1) * 10 + index + 1
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => setActiveApplication(record)}>
            {text}
          </Button>
        </Space>
      )
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Subscription Id',
      dataIndex: ['payments', 'subscriptionId'],
      key: 'subscriptionId',
      skipInjectedSorter: true,
      sorter: (a, b) =>
        a?.payments?.subscriptionId - b?.payments?.subscriptionId
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) =>
        renderApplicationStatusTag({
          text,
          status: record?.status
        })
    },
    {
      title: 'Member Type',
      dataIndex: 'memberType',
      key: 'memberType',
      render: (text, record) => {
        if (isInternalEmail({ email: record?.email }))
          return <Tag color="orange">Nas Academy</Tag>;

        if (record?.payment_details) return <Tag color="cyan">Paid</Tag>;
      }
    },
    {
      title: 'Pending Day(s)',
      dataIndex: 'registrationTime',
      key: 'pendingDays',
      render: (_, record) => {
        const isPending = record.status === applicationStatus.PENDING;

        const pendingDays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: record?.registrationTime
        });

        return isPending ? `${pendingDays} days` : '-';
      },
      skipInjectedSorter: true,
      sorter: (a, b) => {
        const isAPending = a.status === applicationStatus.PENDING;
        const isBPending = b.status === applicationStatus.PENDING;

        const pendingADays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: a?.registrationTime
        });

        const pendingBDays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: b?.registrationTime
        });

        const offsetADays = isAPending ? pendingADays : -1;
        const offsetBDays = isBPending ? pendingBDays : -1;

        return offsetADays - offsetBDays;
      }
    },
    {
      title: 'Status Updated At',
      dataIndex: 'statusUpdatedAt',
      key: 'statusUpdatedAt'
    },
    {
      title: 'Notes',
      dataIndex: 'reviewerNotes',
      key: 'reviewerNotes',
      width: 400
    },
    {
      title: 'Country Field',
      dataIndex: 'country',
      key: 'country'
    },
  ];

  const renderApplicationStatusTag = ({ status, text }) => {
    const tagColor =
      status === applicationStatus.APPROVE
        ? 'green'
        : status === applicationStatus.REJECT
        ? 'volcano'
        : 'blue';
    return <Tag color={tagColor}>{text}</Tag>;
  };

  const onPaginationChange = (data) => {
    setPageNo(data.current);
    setPageSize(data.pageSize);
  };

  const getCourseApplications = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        pageNo,
        pageSize,
        ...filters
      };

      const { data, error } = await fetchCourseApplication(params);

      if (error) throw new Error(error);

      setApplicationList(data.paginatedList);
      setTotalCount(data.totalCount);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  }, [pageNo, pageSize, filters]);

  const getCSVDataList = async () => {
    try {
      setIsCsvDataLoading(true);
      const { data, error } = await fetchCourseApplication({
        ...filters,
        courseCode: form.getFieldValue('courseCode')
      });

      if (error) throw new Error(error);

      const formattedList = getformattedCourseApplicationForCSV({
        list: data.paginatedList
      });
      setCsvDataList(formattedList);
      setIsCsvModalOpen(true);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsCsvDataLoading(false);
    }
  };

  const onFinish = (values) => {
    setPageNo(1);
    if (
      new Date(values?.startDate).getTime() >
      new Date(values?.endDate).getTime()
    ) {
      notification['error']({
        message: 'Error',
        description: 'End Date cannot be before Start Date'
      });
      return;
    }


    const filterData = {
      applicationSearch: values.applicationSearch,
      courseCode: values.courseCode ?? courseList[0].value,
      type: values.type ?? courseList[0].type,
      sortOrder: values.sortOrder ?? sortOrderList[0].value,
      status: values.status,
      sortBy: 'registrationTime',
    };

    setFilters(filterData);
  };

  const resetForm = () => {
    form.resetFields();
    form.submit();
  };

  const clearActiveApplication = () => {
    setActiveApplication(null);
  };

  const onStatusSubmit = async ({ status }) => {
    try {
      setIsStatusLoading(true);
      const params = {
        _id: activeApplication?._id,
        courseCode: activeApplication?.courseCode,
        status,
        reviewerNotes: statusForm.getFieldValue('reviewerNotes')
      };

      const { data, error } = await submitApplicationStatus(params);

      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: data.message
      });

      const updatedActiveApplication = { ...activeApplication, status };
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return updatedActiveApplication;

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };
  const onPaymentLinkSubmit = async () => {
    try {
      setIsStatusLoading(true);
      const params = {
        _id: activeApplication?._id,
        courseCode: activeApplication?.courseCode,
      };

      const { data, error } = await sendPaymentLink(params);

      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: data.message
      });

      const updatedActiveApplication = { ...activeApplication, paymentLinkSent: true };
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return updatedActiveApplication;

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };
  const updateCourseApplication = async (values) => {
    try {
      setIsStatusLoading(true);
      const { data, error } = await updateApplication(values);
      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: `Application updated!`
      });

      const updatedActiveApplication = data;
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return {
              ...updatedActiveApplication,
              payments: application.payments
            };

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const updateReviewersNote = async () => {
    await updateCourseApplication({
      _id: activeApplication?._id,
      reviewerNotes: statusForm.getFieldValue('reviewerNotes')
    });
  };

  const updateApplicationField = async (values) => {
    await updateCourseApplication({
      _id: activeApplication?._id,
      ...values
    });
  };

  const getCourseList = useCallback(async () => {
    try {
      setIsLoading(true);
      const values = await getCourses(1, 1000, {requiresApproval: true} );
      const courses = values?.data?.paginatedList?.map(
        (course) => ({
          value: course?.code,
          text: course?.title
        })
      );
      setCourseList(courses);
      setFilters((prev) => {
        return {
          ...prev,
          courseCode: courses?.[0]?.value
        };
      });
      form.setFieldsValue({ courseCode: courses?.[0]?.value });
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(true);
    }
  }, [form]);

  useEffect(() => {
    if (courseList?.length > 0 && filters?.courseCode) {
      getCourseApplications();
    }
  }, [getCourseApplications, courseList, filters?.courseCode]);

  useEffect(() => {
    getCourseList();
  }, [getCourseList]);

  return (
    <div>
      <div>
        <Row justify="space-between" align="middle">
          <Col>
            <Title>Course Applications</Title>
          </Col>
          <Col>
            <Row gutter={16}>
              <Col>
                <Button
                  type="primary"
                  centered
                  onClick={getCSVDataList}
                  loading={isCsvDataLoading}>
                  Export CSV
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <CourseApplicationsForm
          onFinish={onFinish}
          resetForm={resetForm}
          form={form}
          totalCount={totalCount}
          courseList={courseList}
        />
        <TableWithSorter
          tableLayout="auto"
          scroll={{ x: 1000 }}
          expandable={{ defaultExpandAllRows: true }}
          loading={isLoading}
          dataSource={applicationList}
          columns={columns}
          pagination={{
            total: totalCount,
            defaultPageSize: defaultPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50']
          }}
          onChange={onPaginationChange}
        />
        <CourseApplicationsModal
          activeApplication={activeApplication}
          clearActiveApplication={clearActiveApplication}
          onStatusSubmit={onStatusSubmit}
          onPaymentLinkSubmit={onPaymentLinkSubmit}
          isStatusLoading={isStatusLoading}
          renderApplicationStatusTag={renderApplicationStatusTag}
          statusForm={statusForm}
          updateReviewersNote={updateReviewersNote}
          updateApplicationField={updateApplicationField}
        />
        <Modal
          visible={isCsvModalOpen}
          onCancel={() => setIsCsvModalOpen(false)}
          footer={null}
          centered>
          <Row style={{ marginBottom: '16px' }}>
            <Text strong>
              Note: The filters are applied on the CSV data
            </Text>
          </Row>
          <Row>
            <Button type="primary">
              <CSVLink data={csvDataList} filename="courseApplications">
                Download CSV File
              </CSVLink>
            </Button>
          </Row>
        </Modal>
      </div>
    </div>
  );
};

export default CourseApplicationsPage;
